import type { ReactElement } from 'react';
import { ExhibitionCard, type ExhibitionCardItem } from '@/entities/exhibition';
import { cn } from '@/shared/ui';

/** 첫 로드 중 자리를 잡아 둘 회색 칸 수 — 데스크톱 2열 두 줄. */
const SKELETON_KEYS = Array.from({ length: 4 }, (_, index) => `skeleton-${index}`);

/** 맨 앞 카드를 전폭 큰 카드로 키우는 최소 카드 수 — 그보다 적으면 큰 카드 옆이 비어 보인다. */
const FEATURED_MIN_COUNT = 3;

const GRID_CLASS = 'grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2';

interface ExhibitionCardGridProps {
  readonly exhibitions: readonly ExhibitionCardItem[];
  /** 첫 로드 중 — 카드 자리를 회색 칸으로 잡아 둔다. */
  readonly isPending: boolean;
}

/**
 * 선택한 목적의 기획전 카드 격자(모바일 1열·sm 이상 2열) — 카드가 없는 목적은 준비 중 안내만 보여 준다.
 * 카드가 3장 이상이면 맨 앞 카드를 두 칸 전폭 큰 카드로 세운다.
 */
export const ExhibitionCardGrid = ({
  exhibitions,
  isPending,
}: ExhibitionCardGridProps): ReactElement => {
  if (isPending) {
    // 목록이 채워지는 중임을 보조 기술에 알린다 — 회색 칸 자체는 내용이 없어 읽지 않게 숨긴다.
    return (
      <ul aria-busy="true" className={GRID_CLASS}>
        {SKELETON_KEYS.map((key) => (
          <li key={key} aria-hidden className="flex flex-col">
            <div className="aspect-[4/3] w-full bg-muted/70" />
            <div className="mt-5 h-3.5 w-24 bg-muted" />
            <div className="mt-3 h-6 w-2/3 bg-muted" />
          </li>
        ))}
      </ul>
    );
  }

  if (exhibitions.length === 0) {
    return (
      <p className="border-y border-foreground/10 py-20 text-center text-muted-foreground">
        준비 중인 기획전이 없습니다.
      </p>
    );
  }

  const hasFeatured = exhibitions.length >= FEATURED_MIN_COUNT;

  return (
    <ul className={GRID_CLASS}>
      {exhibitions.map((exhibition, index) => {
        const isFeatured = hasFeatured && index === 0;

        return (
          <li
            key={exhibition.id}
            data-featured={isFeatured ? 'true' : undefined}
            className={cn(isFeatured && 'sm:col-span-2')}
          >
            <ExhibitionCard exhibition={exhibition} isFeatured={isFeatured} />
          </li>
        );
      })}
    </ul>
  );
};
